import React, { useEffect } from "react";

import { useFieldArray, UseFormReturn } from "react-hook-form";

import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { isEmpty } from "lodash/fp";

import type { SelectChangeEvent } from "@mui/material";

import { getSelectedFromOptions } from "@readai/common/components/Dropdown";
import Dropdown, { DropdownMenuItem } from "@readai/common/components/Dropdown";
import type { ConferencingMeetingPlatform } from "@readai/common/types";
import PlusIcon from "@readai/icons/uui/Plus";
import CloseIcon from "@readai/icons/uui/XClose";
import IconButton from "@readai/pulse/components/IconButton";
import Stack, { Row } from "@readai/pulse/components/Stack";

import {
  InputWrapper,
  StyledFormLabel,
  StyledTextField,
} from "@app/booking/components/styles";
import useSchedulingStore from "@app/booking/stores/scheduling";

import { AddGuestButton, BackButton, Header, Heading } from "./styles";

const ADDITIONAL_DETAILS_CHARACTER_LIMIT = 500;
const DEFAULT_GUEST_INPUT = {
  value: "",
};

export interface MeetingDetailsFieldValues {
  guestEmails?: { value: string }[];
  schedulerName: string;
  schedulerEmail: string;
  platform: ConferencingMeetingPlatform;
  details?: string;
}

const MeetingDetails = ({
  form,
  onBack,
  submitButton,
}: {
  form: UseFormReturn<MeetingDetailsFieldValues>;
  onBack?: () => void;
  submitButton: React.ReactNode;
}) => {
  const availableMeetingPlatforms = useSchedulingStore(
    (state) => state.allowedPlatforms,
  );
  const guestsFieldArray = useFieldArray<MeetingDetailsFieldValues>({
    name: "guestEmails",
    control: form.control,
  });

  const {
    formState: { errors },
  } = form;

  const handleAddGuest = () => {
    guestsFieldArray.append(DEFAULT_GUEST_INPUT);
  };

  const handleRemoveGuest = (index: number) => guestsFieldArray.remove(index);

  const handlePlatformChange = (e: SelectChangeEvent) => {
    const newPlatform = e.target.value as ConferencingMeetingPlatform;
    form.setValue("platform", newPlatform);
  };

  const selectedPlatform = form.watch("platform");
  const details = form.watch("details");

  useEffect(() => {
    if (isEmpty(selectedPlatform) && !isEmpty(availableMeetingPlatforms)) {
      form.setValue("platform", availableMeetingPlatforms[0].value);
    }
  }, [selectedPlatform, availableMeetingPlatforms]);

  const remainingDetailsCharacters =
    ADDITIONAL_DETAILS_CHARACTER_LIMIT - (details ?? "").length;

  return (
    <Stack variant="center" spacing={3} sx={{ width: "100%", px: 3, pb: 3 }}>
      <Header>
        <Row variant="center" spacing={1.5}>
          {onBack && <BackButton onClick={onBack} />}
          <Heading>
            <Trans>Add meeting details</Trans>
          </Heading>
        </Row>
      </Header>
      <Stack
        variant="left"
        spacing={2}
        sx={{ width: "100%", maxWidth: "500px" }}
      >
        <InputWrapper>
          <StyledFormLabel required>
            <Trans>Your Name</Trans>
          </StyledFormLabel>
          <StyledTextField
            required
            {...form.register("schedulerName")}
            error={Boolean(errors.schedulerName)}
            helperText={errors.schedulerName?.message}
          />
        </InputWrapper>
        <InputWrapper>
          <StyledFormLabel required>
            <Trans>Your Email</Trans>
          </StyledFormLabel>
          <StyledTextField
            required
            {...form.register("schedulerEmail")}
            error={Boolean(errors.schedulerEmail)}
            helperText={errors.schedulerEmail?.message}
          />
        </InputWrapper>
        <InputWrapper>
          {guestsFieldArray.fields.map((field, i) => (
            <React.Fragment key={`${field.id}-${i}`}>
              <StyledFormLabel>
                <Trans>Guest Email</Trans>
              </StyledFormLabel>
              <Row
                variant="left"
                alignItems="start"
                spacing={1}
                sx={{ width: "100%" }}
              >
                <StyledTextField
                  {...form.register(`guestEmails.${i}.value`)}
                  error={Boolean(errors.guestEmails?.[i])}
                  helperText={errors.guestEmails?.[i]?.value?.message}
                />
                <IconButton
                  onClick={() => handleRemoveGuest(i)}
                  size="small"
                  sx={{ mt: "2px" }}
                >
                  <CloseIcon />
                </IconButton>
              </Row>
            </React.Fragment>
          ))}
          <AddGuestButton
            variant="text"
            size="small"
            startIcon={<PlusIcon />}
            onClick={handleAddGuest}
          >
            <Trans>Add more guests</Trans>
          </AddGuestButton>
        </InputWrapper>
        <InputWrapper>
          <StyledFormLabel required>
            <Trans>Conferencing platform</Trans>
          </StyledFormLabel>
          <Dropdown
            name="platform"
            selectedItem={getSelectedFromOptions(
              availableMeetingPlatforms,
              selectedPlatform,
            )}
            sx={{ "& .MuiSelect-select": { py: 0 } }}
            value={
              selectedPlatform ?? availableMeetingPlatforms[0]?.value ?? ""
            }
            onChange={handlePlatformChange}
            placeholder={t`Select one`}
            required
          >
            {availableMeetingPlatforms.map((p) => (
              <DropdownMenuItem
                key={p.value}
                selected={p.value === selectedPlatform}
                label={p.label}
                value={p.value}
              />
            ))}
          </Dropdown>
        </InputWrapper>
        <InputWrapper>
          <StyledFormLabel>
            <Trans>
              Please share any details that will help us prepare for the meeting
            </Trans>
          </StyledFormLabel>
          <StyledTextField
            multiline
            rows={6}
            {...form.register("details")}
            className="text-field__textarea"
            inputProps={{ maxLength: ADDITIONAL_DETAILS_CHARACTER_LIMIT }}
            helperText={
              remainingDetailsCharacters < ADDITIONAL_DETAILS_CHARACTER_LIMIT
                ? t`Max ${ADDITIONAL_DETAILS_CHARACTER_LIMIT} characters (${remainingDetailsCharacters} remaining)`
                : t`Max ${ADDITIONAL_DETAILS_CHARACTER_LIMIT} characters`
            }
          />
        </InputWrapper>
      </Stack>
      {submitButton}
    </Stack>
  );
};

export default MeetingDetails;
